// /var/www/oszczedzam/client/src/components/dashboard/CategoryChart.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, FormControl, InputLabel, 
  Select, MenuItem, useTheme, alpha, List, ListItem,
  ListItemText, ListItemIcon, Divider
} from '@mui/material';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, 
  Legend, Sector
} from 'recharts';
import { Circle as CircleIcon } from '@mui/icons-material';

// Bardziej czytelna i kontrastowa paleta kolorów
const COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#F44336', '#9C27B0', 
  '#3F51B5', '#00BCD4', '#009688', '#FFEB3B', '#FF5722',
  '#673AB7', '#E91E63', '#8BC34A', '#CDDC39', '#607D8B'
];

function CategoryChart({ transactions }) {
  const theme = useTheme();
  const [chartData, setChartData] = useState([]);
  const [chartType, setChartType] = useState('expense');
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  
  useEffect(() => {
    prepareChartData();
  }, [transactions, chartType]);
  
  // Przygotuj dane do wykresu
  const prepareChartData = () => {
    if (!transactions || transactions.length === 0) {
      setChartData([]);
      setTotalAmount(0);
      return;
    }
    
    // Wybierz tylko przychody lub wydatki
    const filteredTransactions = transactions.filter(t => 
      chartType === 'expense' ? t.amount < 0 : t.amount > 0
    );
    
    // Jeśli nie ma danych po filtrowaniu
    if (filteredTransactions.length === 0) {
      setChartData([]);
      setTotalAmount(0);
      return;
    }
    
    // Pogrupuj transakcje według kategorii
    const categoryMap = {};
    
    filteredTransactions.forEach(transaction => {
      const category = transaction.category || 'Inne';
      const amount = Math.abs(transaction.amount);
      
      if (categoryMap[category]) {
        categoryMap[category] += amount;
      } else {
        categoryMap[category] = amount;
      }
    });
    
    // Oblicz całkowitą sumę
    const total = Object.values(categoryMap).reduce((sum, value) => sum + value, 0);
    setTotalAmount(total);
    
    // Przygotuj dane do wykresu
    const data = Object.entries(categoryMap).map(([name, value]) => ({
      name,
      value,
      percentage: total > 0 ? ((value / total) * 100).toFixed(1) : 0
    }));
    
    // Sortuj od największej do najmniejszej wartości
    data.sort((a, b) => b.value - a.value);
    
    setChartData(data);
  };
  
  // Obsługa zmiany typu transakcji
  const handleTypeChange = (event) => {
    setChartType(event.target.value);
  };
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Custom tooltip dla wykresu
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box sx={{ 
          bgcolor: 'background.paper', 
          p: 1.5, 
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: 1,
          boxShadow: theme.shadows[3]
        }}>
          <Typography variant="subtitle2" fontWeight="bold">{data.name}</Typography>
          <Typography variant="body2">
            {formatAmount(data.value)} ({data.payload.percentage}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };
  
  // Renderuj aktywny sektor
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
    
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 10}
          outerRadius={outerRadius + 12}
          fill={fill}
        />
        <text
          x={cx}
          y={cy - 5}
          textAnchor="middle"
          fill={theme.palette.text.primary}
          fontSize={14}
          fontWeight="bold"
        >
          {payload.name}
        </text>
        <text
          x={cx}
          y={cy + 20}
          textAnchor="middle"
          fill={theme.palette.text.secondary}
        >
          {payload.percentage}%
        </text>
      </g>
    );
  };

  // Obsługa kliknięcia na segment wykresu
  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };
  
  // Niestandardowa legenda
  const renderCustomizedLegend = () => {
    return (
      <List dense>
        {chartData.map((entry, index) => (
          <ListItem 
            key={`item-${index}`}
            onClick={() => setActiveIndex(index)}
            sx={{ 
              cursor: 'pointer',
              bgcolor: activeIndex === index ? alpha(COLORS[index % COLORS.length], 0.1) : 'transparent',
              borderRadius: 1,
              '&:hover': {
                bgcolor: alpha(COLORS[index % COLORS.length], 0.1)
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CircleIcon style={{ color: COLORS[index % COLORS.length] }} />
            </ListItemIcon>
            <ListItemText 
              primary={entry.name}
              secondary={`${formatAmount(entry.value)} (${entry.percentage}%)`}
              primaryTypographyProps={{ 
                variant: 'body2',
                fontWeight: activeIndex === index ? 'bold' : 'normal'
              }}
              secondaryTypographyProps={{ 
                variant: 'caption'
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.1)
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="medium">
          Struktura {chartType === 'expense' ? 'wydatków' : 'przychodów'}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="chart-type-label">Typ</InputLabel>
          <Select
            labelId="chart-type-label"
            id="chart-type"
            value={chartType}
            label="Typ"
            onChange={handleTypeChange}
          >
            <MenuItem value="expense">Wydatki</MenuItem>
            <MenuItem value="income">Przychody</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Suma: {formatAmount(totalAmount)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, mt: 2 }}>
        <Box sx={{ height: 300, width: '100%', maxWidth: { xs: '100%', md: '50%' } }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  activeIndex={activeIndex}
                  activeShape={renderActiveShape}
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={onPieEnter}
                  animationBegin={0}
                  animationDuration={1000}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]} 
                      stroke={theme.palette.background.paper}
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                Brak danych do wyświetlenia
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box sx={{ 
          width: '100%', 
          maxWidth: { xs: '100%', md: '50%' },
          maxHeight: 300,
          overflow: 'auto',
          pl: { xs: 0, md: 2 },
          mt: { xs: 2, md: 0 },
          pt: { xs: 2, md: 0 },
          borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 'none' }
        }}>
          {chartData.length > 0 ? renderCustomizedLegend() : null}
        </Box>
      </Box>
    </Paper>
  );
}

export default CategoryChart;